import { clamp01 } from "@anotacao";
import type { Anotacao, AnexoDoChamado, Chamado, RespostaPublica, SituacaoChamado } from "../types";

/**
 * Cliente das rotas de ACOMPANHAMENTO do chamado (DEV-107).
 *
 * A URL é derivada do `cfg.endpoint` trocando o último segmento — `endpoint` é a URL
 * COMPLETA do POST de bugs (`.../intake/v1/bugs`), não uma base, e é o mesmo truque que
 * `configRemota.ts` já usa para achar `.../config`.
 *
 * Credenciais: o token do relator sempre (`X-Widget-Token`), e o token do chamado no
 * detalhe (`X-Chamado-Token`) ou no corpo, no resumo. Nada de token em URL: ela vaza para
 * log de servidor, histórico e `Referer`.
 *
 * Erro NUNCA lança: o painel de chamados é secundário ao report, e uma falha de rede aqui
 * não pode derrubar o widget. Cada função devolve `null`/`[]` e a UI mostra o aviso.
 */

/** Deriva `.../intake/v1/<sufixo>` a partir do endpoint de bugs. Vazio se inválida. */
function urlDe(endpoint: string, sufixo: string): string {
  try {
    const u = new URL(endpoint);
    u.pathname = u.pathname.replace(/[^/]*$/, sufixo);
    u.search = "";
    return u.href;
  } catch {
    return "";
  }
}

const TIMEOUT_MS = 8000;

async function pedir(url: string, init: RequestInit): Promise<Response | null> {
  if (!url) return null;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: ctrl.signal });
  } catch {
    return null; // offline, CORS, abort
  } finally {
    clearTimeout(timer);
  }
}

/** Headers de credencial: o token do relator sempre, a sessão quando há. */
function comSessao(base: Record<string, string>, sessao?: string): Record<string, string> {
  return sessao ? { ...base, "X-Chamado-Sessao": sessao } : base;
}

export interface ResumoResultado {
  chamados: Chamado[];
  /** Chamados que MUDARAM de código (DEV-302): o storage é re-chaveado com estes pares
      ANTES de qualquer esquecimento. */
  renomeados: Array<{ de: string; para: string }>;
  /** Códigos que o widget tem no storage e o servidor NÃO reconheceu — a UI os esquece. */
  desconhecidos: string[];
  /** Houve falha de rede/servidor (≠ "não há chamado"): a UI distingue as duas coisas. */
  falhou: boolean;
  /** O servidor recusou a sessão apresentada: a UI a esquece e volta a oferecer entrar. */
  sessaoExpirada: boolean;
}

/**
 * Resumo dos chamados de uma lista de tokens — uma requisição serve a lista E a badge.
 *
 * O servidor ignora token que não resolve, então a resposta pode vir menor que a
 * pergunta: comparamos por CÓDIGO para descobrir o que sobrou (chamado apagado, token
 * revogado) e devolvemos em `desconhecidos`. Sem isso o widget mostraria para sempre uma
 * linha que nunca abre.
 *
 * **A pergunta vai por TOKEN e a resposta volta por CÓDIGO**, e desde que o código pode
 * ser reemitido (o card muda de espaço, DEV-302) essa assimetria vira um bug: o servidor
 * resolve o token e devolve o código NOVO, que não casa com nada no storage — o item
 * antigo cairia em `desconhecidos` e seria apagado LEVANDO O TOKEN, a única credencial do
 * caminho anônimo. Daí `codigosAnteriores` vir no payload e o casamento ter duas etapas:
 * pelo código atual, e depois pelos anteriores. O que casar por anterior sai como
 * `renomeados` e **não** entra em `desconhecidos`.
 *
 * @param locais pares código→token do storage (a ordem não importa, o servidor reordena)
 */
export async function buscarResumo(
  endpoint: string,
  widgetToken: string,
  locais: Array<{ codigo: string; token: string }>,
  sessao?: string,
): Promise<ResumoResultado> {
  const vazio = { chamados: [], renomeados: [], desconhecidos: [], falhou: false, sessaoExpirada: false };
  // Sem chamado local E sem sessão não há o que perguntar.
  if (locais.length === 0 && !sessao) return vazio;

  const res = await pedir(urlDe(endpoint, "chamados/resumo"), {
    method: "POST",
    headers: comSessao({ "Content-Type": "application/json", "X-Widget-Token": widgetToken }, sessao),
    // Token vazio é o chamado que veio pela sessão: não é credencial, não vai no corpo.
    body: JSON.stringify({ tokens: locais.map((l) => l.token).filter((t) => t !== "") }),
  });
  if (!res || !res.ok) return { ...vazio, falhou: true };

  let corpo: unknown = null;
  try {
    corpo = await res.json();
  } catch {
    return { ...vazio, falhou: true };
  }
  const dados = corpo as { chamados?: unknown; sessaoExpirada?: unknown } | null;
  if (!Array.isArray(dados?.chamados)) return { ...vazio, falhou: true };

  const chamados = dados.chamados.map(normalizarChamado).filter((c): c is Chamado => c !== null);
  const vistos = new Set(chamados.map((c) => c.codigo));

  // Quem NÃO casou pelo código atual pode ter casado por um anterior. A ordem importa:
  // só procuramos anterior para o chamado cujo código atual o storage desconhece, senão
  // um código antigo que por acaso ainda esteja guardado renomearia um item que já está
  // certo. Um código pertence a um card só, então o primeiro que casar é O par.
  const guardados = new Set(locais.map((l) => l.codigo));
  const renomeados: Array<{ de: string; para: string }> = [];
  for (const c of chamados) {
    if (guardados.has(c.codigo)) continue;
    const antigo = c.codigosAnteriores.find((a) => guardados.has(a));
    if (antigo !== undefined) renomeados.push({ de: antigo, para: c.codigo });
  }
  const renomeadosDe = new Set(renomeados.map((r) => r.de));

  return {
    chamados,
    renomeados,
    desconhecidos: locais.map((l) => l.codigo).filter((c) => !vistos.has(c) && !renomeadosDe.has(c)),
    falhou: false,
    sessaoExpirada: dados.sessaoExpirada === true,
  };
}

/** Detalhe de um chamado + a thread. `null` em qualquer falha (inclusive 404/401). */
export async function buscarChamado(
  endpoint: string,
  widgetToken: string,
  codigo: string,
  chamadoToken: string,
  sessao?: string,
): Promise<Chamado | null> {
  const base = urlDe(endpoint, "chamados/");
  const res = await pedir(base ? `${base}${encodeURIComponent(codigo)}` : "", {
    method: "GET",
    // Sem token local (chamado que veio pela sessão), a sessão é a credencial.
    headers: comSessao(
      chamadoToken ? { "X-Widget-Token": widgetToken, "X-Chamado-Token": chamadoToken } : { "X-Widget-Token": widgetToken },
      sessao,
    ),
  });
  if (!res || !res.ok) return null;
  try {
    return normalizarChamado(await res.json());
  } catch {
    return null;
  }
}

export interface EnvioResposta {
  /** O chamado com a thread já atualizada — o servidor devolve o estado novo inteiro. */
  chamado?: Chamado;
  /** Mensagem legível quando falhou; a UI mostra ESTA, não uma genérica. */
  erro?: string;
}

/**
 * Responde no chamado. Devolve o chamado atualizado (o servidor manda o estado novo
 * completo, então não montamos a bolha localmente e não divergimos da contagem dele).
 *
 * Erro vem como MENSAGEM, não exceção: quem chama precisa mostrá-la ao lado do composer
 * **com o rascunho preservado**. Perder o texto junto com a explicação é o pior desfecho
 * possível aqui — é a lição que o `CommentThread` do front já aprendeu.
 */
export async function enviarResposta(
  endpoint: string,
  widgetToken: string,
  codigo: string,
  chamadoToken: string,
  texto: string,
  sessao?: string,
  imagem?: Blob,
): Promise<EnvioResposta> {
  const base = urlDe(endpoint, "chamados/");
  const cabecalhos: Record<string, string> = { "X-Widget-Token": widgetToken };
  if (chamadoToken) cabecalhos["X-Chamado-Token"] = chamadoToken;

  // Com imagem vai multipart; sem, o JSON de sempre (DEV-213). O `Content-Type` do
  // multipart é do BROWSER: escrevê-lo à mão perde o `boundary` e o servidor recebe um
  // corpo que não sabe abrir — erro que não parece o que é.
  let corpo: BodyInit;
  if (imagem) {
    const fd = new FormData();
    fd.append("texto", texto);
    // O nome vai para o painel de anexos do CARD, onde ele fica ao lado da print do report
    // e do que o time colou. "anexo.webp" não diria nada ali; este diz de onde veio.
    fd.append("imagem", imagem, "resposta-do-cliente.webp");
    corpo = fd;
  } else {
    cabecalhos["Content-Type"] = "application/json";
    corpo = JSON.stringify({ texto });
  }

  const res = await pedir(base ? `${base}${encodeURIComponent(codigo)}/comentarios` : "", {
    method: "POST",
    headers: comSessao(cabecalhos, sessao),
    body: corpo,
  });
  if (!res) return { erro: "Sem conexão. Sua mensagem continua aqui — tente de novo." };

  if (!res.ok) {
    if (res.status === 429) return { erro: "Muitas mensagens seguidas. Aguarde um minuto e tente de novo." };
    return { erro: (await mensagemDeErro(res)) ?? "Não foi possível enviar sua mensagem agora." };
  }

  try {
    const chamado = normalizarChamado(await res.json());
    // 201 sem corpo utilizável: a mensagem FOI gravada, então não dizemos que falhou —
    // só não temos o estado novo para pintar.
    return chamado ? { chamado } : {};
  } catch {
    return {};
  }
}

/**
 * A mensagem do backend, quando ela é acionável. O shape é `{message}` e, na validação,
 * `{message, errors:{campo:[…]}}` — o 422 de card fechado chega assim, e é justamente o
 * caso em que a explicação do servidor vale mais que qualquer texto nosso.
 */
async function mensagemDeErro(res: Response): Promise<string | null> {
  try {
    const corpo = (await res.json()) as { message?: unknown; errors?: Record<string, unknown> } | null;
    const primeiro = corpo?.errors ? Object.values(corpo.errors)[0] : null;
    if (Array.isArray(primeiro) && typeof primeiro[0] === "string") return primeiro[0];
    return typeof corpo?.message === "string" && corpo.message !== "" ? corpo.message : null;
  } catch {
    return null;
  }
}

/**
 * O BINÁRIO de um anexo citado na descrição, como Blob.
 *
 * Vem por `fetch` com header, e não por `<img src>`, porque `<img>` não manda header e o
 * token do chamado nunca vai na URL (a mesma regra do resto do intake). Quem chama
 * transforma em objectURL — e é quem tem de revogá-lo depois.
 */
export async function buscarAnexo(
  endpoint: string,
  widgetToken: string,
  codigo: string,
  anexoId: string,
  chamadoToken: string,
  sessao?: string,
): Promise<Blob | null> {
  const base = urlDe(endpoint, "chamados/");
  if (!base) return null;
  const res = await pedir(`${base}${encodeURIComponent(codigo)}/anexos/${encodeURIComponent(anexoId)}`, {
    method: "GET",
    headers: comSessao(
      chamadoToken ? { "X-Widget-Token": widgetToken, "X-Chamado-Token": chamadoToken } : { "X-Widget-Token": widgetToken },
      sessao,
    ),
  });
  if (!res || !res.ok) return null;
  try {
    return await res.blob();
  } catch {
    return null;
  }
}

const SITUACOES: SituacaoChamado[] = ["recebido", "em_andamento", "resolvido", "encerrado", "cancelado"];

/**
 * Valida o que veio do fio. Faltando o essencial (código/título/rótulo) devolve null — é
 * melhor a linha não aparecer do que aparecer quebrada.
 *
 * A `situacao` é tratada como abertura de contrato: código desconhecido cai em
 * `"recebido"` para a LÓGICA (cor, ícone), enquanto o TEXTO exibido continua sendo o
 * `situacaoLabel` do servidor. Assim um valor novo no api aparece certo num widget velho
 * — que é o cenário real, porque o bundle vive cacheado na página do cliente.
 */
function normalizarChamado(bruto: unknown): Chamado | null {
  if (!bruto || typeof bruto !== "object") return null;
  const o = bruto as Record<string, unknown>;
  if (typeof o.codigo !== "string" || o.codigo === "") return null;
  if (typeof o.titulo !== "string") return null;
  if (typeof o.situacaoLabel !== "string" || o.situacaoLabel === "") return null;

  const situacao = SITUACOES.includes(o.situacao as SituacaoChamado)
    ? (o.situacao as SituacaoChamado)
    : "recebido";

  const chamado: Chamado = {
    codigo: o.codigo,
    titulo: o.titulo,
    situacao,
    situacaoLabel: o.situacaoLabel,
    abertoEm: typeof o.abertoEm === "string" ? o.abertoEm : "",
    respostas: typeof o.respostas === "number" && o.respostas > 0 ? Math.floor(o.respostas) : 0,
    ultimaRespostaEm: typeof o.ultimaRespostaEm === "string" ? o.ultimaRespostaEm : null,
    fechado: o.fechado === true,
    // Card que o time compartilhou (DEV-245). `=== true` e não `!!`: servidor antigo não
    // manda a chave, e ausente tem de virar "é chamado dele" — o default seguro é o que
    // já era verdade antes de a seção existir.
    compartilhado: o.compartilhado === true,
    // Códigos que este chamado já teve (DEV-302). Servidor antigo não manda a chave, e
    // `[]` é a leitura certa dela: sem reemissão, nenhum chamado tem código anterior.
    codigosAnteriores: Array.isArray(o.codigosAnteriores)
      ? o.codigosAnteriores.filter((c): c is string => typeof c === "string" && c !== "")
      : [],
  };

  // A chave só existe no DETALHE — ausente ≠ thread vazia, e confundir as duas faria a
  // lista parecer um chamado sem nenhuma resposta.
  if (Array.isArray(o.respostasThread)) {
    chamado.respostasThread = o.respostasThread
      .map(normalizarResposta)
      .filter((r): r is RespostaPublica => r !== null);
  }

  // `descricao`/`anexos` também são só do detalhe, pelo mesmo motivo: ausente é "não
  // perguntei", vazio é "não tem". A bolha do report distingue as duas coisas.
  if (typeof o.descricao === "string") chamado.descricao = o.descricao;
  if (Array.isArray(o.anexos)) {
    chamado.anexos = o.anexos
      .map(normalizarAnexo)
      .filter((a): a is AnexoDoChamado => a !== null);
  }

  return chamado;
}

/** Um anexo do detalhe. Sem `id` ou `nome` não há o que buscar nem o que rotular. */
function normalizarAnexo(bruto: unknown): AnexoDoChamado | null {
  if (!bruto || typeof bruto !== "object") return null;
  const o = bruto as Record<string, unknown>;
  if (typeof o.id !== "string" || o.id === "") return null;

  const anexo: AnexoDoChamado = {
    id: o.id,
    nome: typeof o.nome === "string" && o.nome !== "" ? o.nome : "anexo",
    mime: typeof o.mime === "string" ? o.mime : "",
    tamanhoBytes: typeof o.tamanhoBytes === "number" && o.tamanhoBytes > 0 ? Math.floor(o.tamanhoBytes) : 0,
  };

  // Só o anexo da PRINT traz marcas (DEV-125). Chave ausente é o caso comum — anexo que o
  // time colou depois não tem anotação, e nada aqui deve inventar uma.
  if (Array.isArray(o.anotacoes)) {
    const marcas = o.anotacoes.map(normalizarAnotacao).filter((a): a is Anotacao => a !== null);
    if (marcas.length > 0) anexo.anotacoes = marcas;
  }

  return anexo;
}

/**
 * Uma marca do report. O servidor já saneia, mas isto é a segunda rede: a origem do dado é
 * um POST ANÔNIMO cujo elemento o intake não valida, e aqui ele vai virar HTML na página do
 * CLIENTE. Tipo desconhecido, coordenada não-numérica ou NaN vira descarte; o resto é
 * fixado na faixa 0–1, que é o contrato de coordenada do widget.
 */
function normalizarAnotacao(bruto: unknown): Anotacao | null {
  if (!bruto || typeof bruto !== "object") return null;
  const o = bruto as Record<string, unknown>;
  const tipo = o.tipo;
  if (tipo !== "pin" && tipo !== "rect" && tipo !== "arrow") return null;

  const x = fracao(o.x);
  const y = fracao(o.y);
  if (x === null || y === null) return null;

  const marca: Anotacao = { tipo, x, y };
  const x2 = fracao(o.x2);
  const y2 = fracao(o.y2);
  if (x2 !== null) marca.x2 = x2;
  if (y2 !== null) marca.y2 = y2;
  if (typeof o.comentario === "string" && o.comentario.trim() !== "") {
    marca.comentario = o.comentario.trim().slice(0, 500);
  }

  return marca;
}

/** Coordenada normalizada: número finito, fixado em 0–1. */
function fracao(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? clamp01(v) : null;
}

function normalizarResposta(bruto: unknown): RespostaPublica | null {
  if (!bruto || typeof bruto !== "object") return null;
  const o = bruto as Record<string, unknown>;
  if (typeof o.id !== "string" || typeof o.texto !== "string") return null;

  const resposta: RespostaPublica = {
    id: o.id,
    texto: o.texto,
    criadoEm: typeof o.criadoEm === "string" ? o.criadoEm : "",
    editadoEm: typeof o.editadoEm === "string" ? o.editadoEm : null,
    autorNome: typeof o.autorNome === "string" && o.autorNome !== "" ? o.autorNome : "Suporte",
    doReporter: o.doReporter === true,
  };

  // As imagens da resposta (DEV-173), pelo mesmo normalizador do anexo do report. A chave
  // só nasce quando há item válido: a bolha desenha a grade por `length`, e a diferença
  // entre "não veio" e "veio vazia" não muda nada ali.
  if (Array.isArray(o.anexos)) {
    const anexos = o.anexos.map(normalizarAnexo).filter((a): a is AnexoDoChamado => a !== null);
    if (anexos.length > 0) resposta.anexos = anexos;
  }

  return resposta;
}
